import {
  SUPPORTED_LOCALES,
  flattenCatalog,
} from './catalog-registry';
import { browserLogger } from '../runtime/logging/browser-logger';

export interface RenderedTranslationKey {
  key: string;
  location: 'text' | 'placeholder' | 'aria-label' | 'title' | 'alt';
  tagName?: string;
  route?: string;
}

const catalogKeys = new Set<string>();
for (const key of flattenCatalog(SUPPORTED_LOCALES.en.catalog).keys()) {
  catalogKeys.add(key);
  const dot = key.indexOf('.');
  if (dot > 0) catalogKeys.add(`${key.slice(0, dot)}:${key.slice(dot + 1)}`);
}

const KEY_TOKEN = /\b[A-Za-z][A-Za-z0-9_-]*(?::[A-Za-z0-9_.-]+|\.[A-Za-z0-9_.-]+)\b/g;
const renderedKeys = new Map<string, RenderedTranslationKey>();
const IGNORED_TAGS = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'TEMPLATE']);
const PRESENTATION_ATTRIBUTES = ['placeholder', 'aria-label', 'title', 'alt'] as const;
let observer: MutationObserver | null = null;

function keyCandidates(value: string | null | undefined): string[] {
  const text = value?.trim();
  if (!text) return [];
  const candidates = new Set<string>();
  if (catalogKeys.has(text)) candidates.add(text);
  for (const match of text.matchAll(KEY_TOKEN)) {
    if (catalogKeys.has(match[0])) candidates.add(match[0]);
  }
  return [...candidates];
}

function identity(entry: RenderedTranslationKey): string {
  return `${entry.location}:${entry.key}:${entry.route ?? ''}:${entry.tagName ?? ''}`;
}

function record(entry: RenderedTranslationKey): void {
  const id = identity(entry);
  if (renderedKeys.has(id)) return;
  renderedKeys.set(id, entry);
  browserLogger.warnOnce(
    `raw-i18n-key:${id}`,
    'i18n.raw_key_rendered',
    'Translation key reached user-visible presentation',
    { ...entry },
  );
}

function route(): string | undefined {
  return typeof window !== 'undefined' ? window.location.pathname : undefined;
}

function ignoredElement(element: Element | null): boolean {
  return Boolean(
    element?.closest('script, style, code, pre, template, [data-i18n-debug-key="true"]'),
  );
}

function inspectTextNode(node: Text): void {
  const parent = node.parentElement;
  if (!parent || ignoredElement(parent) || IGNORED_TAGS.has(parent.tagName)) return;
  for (const key of keyCandidates(node.textContent)) {
    record({ key, location: 'text', tagName: parent.tagName.toLowerCase(), route: route() });
  }
}

function inspectElement(element: Element): void {
  if (ignoredElement(element) || IGNORED_TAGS.has(element.tagName)) return;
  for (const attribute of PRESENTATION_ATTRIBUTES) {
    for (const key of keyCandidates(element.getAttribute(attribute))) {
      record({
        key,
        location: attribute,
        tagName: element.tagName.toLowerCase(),
        route: route(),
      });
    }
  }
}

function inspectNode(node: Node): void {
  if (node.nodeType === Node.TEXT_NODE) {
    inspectTextNode(node as Text);
    return;
  }
  if (!(node instanceof Element)) return;
  inspectElement(node);
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();
  while (current) {
    if (current.nodeType === Node.TEXT_NODE) inspectTextNode(current as Text);
    else if (current instanceof Element) inspectElement(current);
    current = walker.nextNode();
  }
}

export function scanRenderedTranslationKeys(root: Node = document.body): RenderedTranslationKey[] {
  if (typeof document === 'undefined' || !root) return getRenderedTranslationKeys();
  inspectNode(root);
  return getRenderedTranslationKeys();
}

export function getRenderedTranslationKeys(): RenderedTranslationKey[] {
  return [...renderedKeys.values()].sort((left, right) => identity(left).localeCompare(identity(right)));
}

export function clearRenderedTranslationKeys(): void {
  renderedKeys.clear();
}

/**
 * Per-test suppression flag. Tests that mock `react-i18next` (causing raw
 * translation keys to appear in the DOM) set this to `true` to suppress the
 * raw-key safety assertion. Must be reset to `false` after the test.
 */
let rawKeyCheckSuppressed = false;

export function suppressRawKeyCheck(value: boolean): void {
  rawKeyCheckSuppressed = value;
}

export function isRawKeyCheckSuppressed(): boolean {
  return rawKeyCheckSuppressed;
}

export function assertNoRenderedTranslationKeys(): void {
  if (rawKeyCheckSuppressed) return;
  const entries = getRenderedTranslationKeys();
  if (entries.length === 0) return;
  throw new Error(
    `i18n: ${entries.length} raw translation key(s) reached presentation:\n${entries
      .map((entry) =>
        `  - ${entry.key} location=${entry.location}` +
        `${entry.tagName ? ` tag=${entry.tagName}` : ''}` +
        `${entry.route ? ` route=${entry.route}` : ''}`,
      )
      .join('\n')}`,
  );
}

/** Install only in development/test; production has zero observer overhead. */
export function installRawTranslationKeyDetector(): void {
  if (!(import.meta.env.DEV || import.meta.env.MODE === 'test')) return;
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined' || observer) return;

  if (document.body) scanRenderedTranslationKeys(document.body);
  observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'characterData') inspectNode(mutation.target);
      if (mutation.type === 'attributes' && mutation.target instanceof Element) {
        inspectElement(mutation.target);
      }
      for (const added of mutation.addedNodes) inspectNode(added);
    }
  });
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: [...PRESENTATION_ATTRIBUTES],
  });
}

export function uninstallRawTranslationKeyDetector(): void {
  observer?.disconnect();
  observer = null;
}
